/**
 * cache/dev-assert.ts — dev-mode `setQueryData` guard (T6018).
 *
 * Walks the data argument recursively and throws on the first non-JSON-safe
 * shape (Date / Map / Set / RegExp / BigInt / class-instance / function /
 * symbol / undefined). The throw includes a dotted path
 * (`data.timestamps[3].createdAt`) so the consumer can fix the leak before
 * persistence corruption.
 *
 * Production builds dead-code-eliminate the check via the leading
 * `NODE_ENV === 'production'` early-return.
 */

import type { QueryClient } from '@tanstack/react-query';
import { SdkError } from '../errors/SdkError.js';

declare const process: { env: { NODE_ENV?: string } };

const BANNED_PROTOTYPES: ReadonlySet<object> = new Set<object>([
  Date.prototype,
  Map.prototype,
  Set.prototype,
  RegExp.prototype,
]);

function isClassInstance(v: object): boolean {
  const proto = Object.getPrototypeOf(v) as object | null;
  return proto !== Object.prototype && proto !== Array.prototype && proto !== null;
}

/**
 * Walks `value` recursively, throws on the first non-JSON-safe shape. Path
 * is the dotted accessor from the root, e.g. `data.timestamps[3].createdAt`.
 * Production: dead-code-eliminated via the NODE_ENV check.
 */
export function assertJsonSafe(value: unknown, path = 'data'): void {
  if (typeof process !== 'undefined' && process.env?.NODE_ENV === 'production') {
    return;
  }
  if (value === null) return;
  const t = typeof value;
  if (t === 'string' || t === 'number' || t === 'boolean') return;
  if (t === 'bigint') {
    throw new Error(`Non-JSON-safe value at ${path}: BigInt — use string for D9 amounts`);
  }
  if (t === 'undefined') {
    throw new Error(
      `Non-JSON-safe value at ${path}: undefined — JSON.stringify drops keys with undefined values`,
    );
  }
  if (t === 'function') {
    throw new Error(`Non-JSON-safe value at ${path}: function — not persistable`);
  }
  if (t === 'symbol') {
    throw new Error(`Non-JSON-safe value at ${path}: symbol — not persistable`);
  }
  if (t !== 'object') {
    throw new Error(`Non-JSON-safe value at ${path}: ${t}`);
  }

  const v = value as object;
  const proto = Object.getPrototypeOf(v) as object | null;
  if (proto !== null && BANNED_PROTOTYPES.has(proto)) {
    const name = (proto.constructor as { name?: string } | undefined)?.name ?? 'Object';
    throw new Error(
      `Non-JSON-safe value at ${path}: ${name} — use ISO string / array / plain object`,
    );
  }
  if (isClassInstance(v) && !Array.isArray(v)) {
    const cls = (v.constructor as { name?: string } | undefined)?.name ?? 'Object';
    throw new Error(`Non-JSON-safe value at ${path}: class instance ${cls} — use a plain object`);
  }
  if (Array.isArray(v)) {
    for (let i = 0; i < v.length; i++) {
      assertJsonSafe(v[i], `${path}[${i}]`);
    }
    return;
  }
  for (const [k, val] of Object.entries(v)) {
    assertJsonSafe(val, `${path}.${k}`);
  }
}

/**
 * Wrap a QueryClient so every `setQueryData` call runs through
 * `assertJsonSafe`. Both literal and updater-function calls are covered;
 * the updater path applies the check to the next value (what would
 * actually be cached).
 *
 * Production builds: the inner check is a no-op (assertJsonSafe early-returns),
 * but we still wrap so production and dev share the same call shape — the
 * minifier collapses the wrapper away.
 */
export function wrapQueryClientForDevAsserts(qc: QueryClient): QueryClient {
  if (typeof process !== 'undefined' && process.env?.NODE_ENV === 'production') {
    return qc;
  }

  type AnyUpdater = unknown | ((prev: unknown) => unknown);
  type SetQueryDataFn = (key: readonly unknown[], updater: AnyUpdater, opts?: unknown) => unknown;
  const origSet = qc.setQueryData.bind(qc) as unknown as SetQueryDataFn;

  const wrappedSet: SetQueryDataFn = (key, updater, opts) => {
    if (typeof updater === 'function') {
      const wrappedUpdater = (prev: unknown): unknown => {
        const next = (updater as (p: unknown) => unknown)(prev);
        assertJsonSafe(next);
        return next;
      };
      return origSet(key, wrappedUpdater, opts);
    }
    assertJsonSafe(updater);
    return origSet(key, updater, opts);
  };

  // Rebinding setQueryData on the running instance. The TanStack type is
  // a heavily-overloaded generic; we restore it via a structural cast.
  (qc as unknown as { setQueryData: SetQueryDataFn }).setQueryData = wrappedSet;

  return qc;
}

// =============================================================================
// T1006 · SdkError variant with `$`-prefixed paths
// =============================================================================
//
// T6018 ships `assertJsonSafe` that throws plain `Error` with `data.`-prefixed
// paths. T1006's acceptance criteria mandate `SdkError(category='schema',
// code='PROTO_DECODE_FAILED')` with `$`-prefixed paths so the existing error
// pipeline (Better Stack telemetry + category routing) handles violations
// uniformly. Both variants share the same walker; the wrapper below routes
// the throw through `SdkError`.

function makeSchemaViolation(path: string, kind: string): SdkError {
  return new SdkError({
    category: 'schema',
    code: 'PROTO_DECODE_FAILED',
    devMessage: `Non-JSON-safe value at ${path}: ${kind}.`,
    userMessage: 'errors.schema.invalid',
    correlationId:
      typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
        ? crypto.randomUUID()
        : 'dev-assert',
  });
}

/**
 * Schema-flavoured variant of `assertJsonSafe` (T1006).
 *
 * Throws `SdkError({ category: 'schema', code: 'PROTO_DECODE_FAILED' })`
 * with `$`-prefixed paths (`$.nested.deep.bad`). Used by SDKProvider's
 * setQueryData wrapper so violations surface in the same pipeline as
 * other schema errors (Better Stack category routing, boundary catchers).
 *
 * Same DCE semantics — body is a no-op in production.
 */
export function assertJsonSafeSchema(value: unknown, path: string = '$'): void {
  if (typeof process !== 'undefined' && process.env?.NODE_ENV === 'production') {
    return;
  }
  walkSchema(value, path, new WeakSet());
}

function walkSchema(value: unknown, path: string, seen: WeakSet<object>): void {
  if (value === null || value === undefined) return;
  const t = typeof value;
  if (t === 'string' || t === 'number' || t === 'boolean') return;
  if (t === 'bigint') throw makeSchemaViolation(path, 'bigint');
  if (t === 'function') throw makeSchemaViolation(path, 'function');
  if (t === 'symbol') throw makeSchemaViolation(path, 'symbol');
  if (t !== 'object') throw makeSchemaViolation(path, t);

  const v = value as object;
  if (seen.has(v)) throw makeSchemaViolation(path, 'cycle');
  seen.add(v);

  const tag = Object.prototype.toString.call(v);
  if (tag === '[object Date]') throw makeSchemaViolation(path, 'Date');
  if (tag === '[object Map]') throw makeSchemaViolation(path, 'Map');
  if (tag === '[object Set]') throw makeSchemaViolation(path, 'Set');
  if (tag === '[object RegExp]') throw makeSchemaViolation(path, 'RegExp');

  if (Array.isArray(v)) {
    for (let i = 0; i < v.length; i++) walkSchema(v[i], `${path}[${i}]`, seen);
    return;
  }
  const proto = Object.getPrototypeOf(v);
  if (proto !== Object.prototype && proto !== null) {
    const cls = (v as { constructor?: { name?: string } }).constructor?.name ?? 'unknown';
    throw makeSchemaViolation(path, `class instance (${cls})`);
  }
  for (const [k, child] of Object.entries(v)) {
    walkSchema(child, `${path}.${k}`, seen);
  }
}

/**
 * Wrap a QueryClient with the SdkError-variant assertion (T1006).
 *
 * Differs from `wrapQueryClientForDevAsserts` only in the error shape —
 * this is the load-bearing integration for the SDKProvider's dev mode
 * since it routes violations through the SdkError pipeline.
 */
/**
 * One-shot dev-safe `setQueryData` wrapper (T512 public surface).
 *
 * Runs the JSON-safety check on the value (or the updater's return) and
 * then forwards to `qc.setQueryData`. Production: assertion is a no-op
 * via the early-return in `assertJsonSafe`; the wrapper itself collapses
 * away under minification because the only branch logic is the inner
 * NODE_ENV check.
 *
 * Use this when you don't want to wrap the entire `QueryClient` — e.g.
 * tests that target one specific write site.
 */
export function safeSetQueryData<T>(
  qc: QueryClient,
  key: readonly unknown[],
  updater: T | ((prev: T | undefined) => T | undefined),
): T | undefined {
  type AnyUpdater = unknown | ((prev: unknown) => unknown);
  type SetQueryDataFn = (k: readonly unknown[], u: AnyUpdater, o?: unknown) => unknown;
  const set = qc.setQueryData.bind(qc) as unknown as SetQueryDataFn;
  if (typeof updater === 'function') {
    const wrapped = (prev: unknown): unknown => {
      const next = (updater as (p: unknown) => unknown)(prev);
      assertJsonSafe(next);
      return next;
    };
    return set(key, wrapped) as T | undefined;
  }
  assertJsonSafe(updater);
  return set(key, updater) as T | undefined;
}

export function wrapQueryClientForSchemaAsserts(qc: QueryClient): QueryClient {
  if (typeof process !== 'undefined' && process.env?.NODE_ENV === 'production') {
    return qc;
  }

  type AnyUpdater = unknown | ((prev: unknown) => unknown);
  type SetQueryDataFn = (key: readonly unknown[], updater: AnyUpdater, opts?: unknown) => unknown;
  const origSet = qc.setQueryData.bind(qc) as unknown as SetQueryDataFn;

  const wrappedSet: SetQueryDataFn = (key, updater, opts) => {
    if (typeof updater === 'function') {
      const wrappedUpdater = (prev: unknown): unknown => {
        const next = (updater as (p: unknown) => unknown)(prev);
        assertJsonSafeSchema(next);
        return next;
      };
      return origSet(key, wrappedUpdater, opts);
    }
    assertJsonSafeSchema(updater);
    return origSet(key, updater, opts);
  };

  (qc as unknown as { setQueryData: SetQueryDataFn }).setQueryData = wrappedSet;

  return qc;
}
